import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Trash, ListChecks, ChartLineUp, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Semaforo, StatusBadge } from "../components/Semaforo";
import { COMMITMENT_TIPOS, COMMITMENT_ESTADOS, RELATED_ENTITY_TYPES } from "../lib/constants";
import { formatDate, formatCLP } from "../lib/format";
import { useConfirm } from "../components/ConfirmDialog";

export default function Commitments() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(empty);
  const [filters, setFilters] = useState({
    estado: params.get("estado") || "",
    semaforo: params.get("semaforo") || "",
    tipo: params.get("tipo") || "",
    related_entity_type: params.get("related_entity_type") || "",
    related_entity_id: params.get("related_entity_id") || "",
  });

  function empty() {
    return {
      name: "", description: "", tipo: "operacional", origen: "",
      related_entity_type: params.get("related_entity_type") || "",
      related_entity_id: params.get("related_entity_id") || "",
      project_id: "",
      responsable_interno: "", responsable_externo: "",
      responsable_email: "",
      fecha_inicio: "", fecha_compromiso: "", fecha_cumplimiento: "",
      estado: "pendiente", beneficiarios_directos: 0, beneficiarios_indirectos: 0, inversion: 0, notas: "",
    };
  }

  const load = async (overrides) => {
    const f = overrides || filters;
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    const { data } = await api.get("/commitments", { params: p });
    setItems(data);
  };

  // Initial data load (catalogs)
  useEffect(() => {
    api.get("/communities").then((r) => setCommunities(r.data));
    api.get("/authorities").then((r) => setAuthorities(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  // Re-sync filters from URL query params whenever they change (e.g. sidebar click).
  useEffect(() => {
    const next = {
      estado: params.get("estado") || "",
      semaforo: params.get("semaforo") || "",
      tipo: params.get("tipo") || "",
      related_entity_type: params.get("related_entity_type") || "",
      related_entity_id: params.get("related_entity_id") || "",
    };
    setFilters(next);
    load(next);
    if (params.get("new") === "1") { setForm(empty()); setOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submit = async () => {
    try {
      const payload = { ...form };
      payload.beneficiarios_directos = +payload.beneficiarios_directos || 0;
      payload.beneficiarios_indirectos = +payload.beneficiarios_indirectos || 0;
      payload.inversion = +payload.inversion || 0;
      payload.fecha_inicio = payload.fecha_inicio || null;
      payload.fecha_compromiso = payload.fecha_compromiso || null;
      payload.fecha_cumplimiento = payload.fecha_cumplimiento || null;
      if (!payload.responsable_email) payload.responsable_email = null;
      if (!payload.related_entity_type) { payload.related_entity_type = null; payload.related_entity_id = null; }
      if (!payload.related_entity_id) payload.related_entity_id = null;
      if (!payload.project_id) payload.project_id = null;
      await api.post("/commitments", payload);
      toast.success("Compromiso creado");
      setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Eliminar compromiso", description: "¿Deseas eliminar este compromiso? Esta acción no se puede deshacer.", danger: true }))) return;
    await api.delete(`/commitments/${id}`); load();
  };

  const updateState = async (c, estado) => {
    const payload = {
      name: c.name, description: c.description, tipo: c.tipo, origen: c.origen,
      related_entity_type: c.related_entity_type, related_entity_id: c.related_entity_id,
      project_id: c.project_id, rca_id: c.rca_id,
      responsable_interno: c.responsable_interno, responsable_externo: c.responsable_externo,
      fecha_compromiso: c.fecha_compromiso, fecha_cumplimiento: c.fecha_cumplimiento,
      estado, beneficiarios_directos: c.beneficiarios_directos, beneficiarios_indirectos: c.beneficiarios_indirectos,
      inversion: c.inversion, notas: c.notas,
    };
    await api.put(`/commitments/${c.id}`, payload);
    load();
  };

  const relatedEntities = useMemo(() => {
    if (form.related_entity_type === "community") return communities;
    if (form.related_entity_type === "authority") return authorities;
    return [];
  }, [form.related_entity_type, communities, authorities]);

  const timeline = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const da = a.fecha_compromiso || "9999"; const db = b.fecha_compromiso || "9999";
      return da.localeCompare(db);
    });
    return sorted;
  }, [items]);

  return (
    <div data-testid="commitments-page">
      <PageHeader
        title="Compromisos"
        description="Tabla transversal de compromisos RCA, operacionales, voluntarios, comunitarios, ambientales e institucionales."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-commitment-btn" className="bg-slate-900 hover:bg-slate-800 text-white"><Plus weight="bold" /> Nuevo compromiso</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Nuevo compromiso</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nombre</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="commit-name-input" />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{COMMITMENT_TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{COMMITMENT_ESTADOS.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Vincular a</Label>
                  <Select value={form.related_entity_type || "__none__"} onValueChange={(v) => setForm({ ...form, related_entity_type: v === "__none__" ? "" : v, related_entity_id: "" })}>
                    <SelectTrigger><SelectValue placeholder="Sin vínculo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin vínculo</SelectItem>
                      <SelectItem value="community">Comunidad</SelectItem>
                      <SelectItem value="authority">Autoridad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Entidad</Label>
                  <Select value={form.related_entity_id || "__none__"} onValueChange={(v) => setForm({ ...form, related_entity_id: v === "__none__" ? "" : v })} disabled={!form.related_entity_type}>
                    <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {relatedEntities.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Proyecto</Label>
                  <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Ninguno</SelectItem>
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Origen</Label>
                  <Input value={form.origen} onChange={(e) => setForm({ ...form, origen: e.target.value })} />
                </div>
                <div>
                  <Label>Responsable interno</Label>
                  <Input value={form.responsable_interno} onChange={(e) => setForm({ ...form, responsable_interno: e.target.value })} />
                </div>
                <div>
                  <Label>Responsable externo</Label>
                  <Input value={form.responsable_externo} onChange={(e) => setForm({ ...form, responsable_externo: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Email(s) responsable — separa con coma para varios</Label>
                  <Input type="text" value={form.responsable_email} onChange={(e) => setForm({ ...form, responsable_email: e.target.value })} placeholder="nombre@empresa.cl, otro@empresa.cl" />
                </div>
                <div>
                  <Label>Fecha inicio</Label>
                  <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} />
                </div>
                <div>
                  <Label>Fecha compromiso (vencimiento)</Label>
                  <Input type="date" value={form.fecha_compromiso} onChange={(e) => setForm({ ...form, fecha_compromiso: e.target.value })} />
                </div>
                <div>
                  <Label>Fecha cumplimiento</Label>
                  <Input type="date" value={form.fecha_cumplimiento} onChange={(e) => setForm({ ...form, fecha_cumplimiento: e.target.value })} />
                </div>
                <div>
                  <Label>Beneficiarios directos</Label>
                  <Input type="number" value={form.beneficiarios_directos} onChange={(e) => setForm({ ...form, beneficiarios_directos: e.target.value })} />
                </div>
                <div>
                  <Label>Beneficiarios indirectos</Label>
                  <Input type="number" value={form.beneficiarios_indirectos} onChange={(e) => setForm({ ...form, beneficiarios_indirectos: e.target.value })} />
                </div>
                <div>
                  <Label>Inversión (CLP)</Label>
                  <Input type="number" value={form.inversion} onChange={(e) => setForm({ ...form, inversion: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Descripción</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Notas</Label>
                  <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={submit} className="bg-slate-900 text-white" data-testid="save-commitment-btn">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-6 py-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Estado</Label>
            <Select value={filters.estado || "__all__"} onValueChange={(v) => setFilters({ ...filters, estado: v === "__all__" ? "" : v })}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {COMMITMENT_ESTADOS.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Tipo</Label>
            <Select value={filters.tipo || "__all__"} onValueChange={(v) => setFilters({ ...filters, tipo: v === "__all__" ? "" : v })}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                {COMMITMENT_TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Semáforo</Label>
            <Select value={filters.semaforo || "__all__"} onValueChange={(v) => setFilters({ ...filters, semaforo: v === "__all__" ? "" : v })}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="verde">Verde</SelectItem>
                <SelectItem value="amarillo">Amarillo</SelectItem>
                <SelectItem value="rojo">Rojo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="secondary" onClick={() => load()} data-testid="apply-filters-btn">Aplicar</Button>
          <Button variant="ghost" onClick={() => { setFilters({ estado:"", semaforo:"", tipo:"", related_entity_type:"", related_entity_id:"" }); setParams({}); }}>Limpiar</Button>
        </div>

        <Tabs defaultValue="table">
          <TabsList>
            <TabsTrigger value="table"><ListChecks className="mr-1.5" /> Tabla</TabsTrigger>
            <TabsTrigger value="timeline"><ChartLineUp className="mr-1.5" /> Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="table">
            <div className="rounded-md border border-slate-200 bg-white overflow-hidden mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Compromiso</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Responsable</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Monto comprometido</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Semáforo</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Sin compromisos.</TableCell></TableRow>}
                  {items.map(c => (
                    <TableRow key={c.id} data-testid={`commit-row-${c.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/compromisos/${c.id}`)}>
                      <TableCell className="font-medium">
                        <Link to={`/compromisos/${c.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{c.name}</Link>
                        <div className="text-xs text-slate-500">{c.origen || ""}</div>
                      </TableCell>
                      <TableCell><StatusBadge tone="info">{c.tipo}</StatusBadge></TableCell>
                      <TableCell>{c.responsable_interno || "—"}</TableCell>
                      <TableCell>{formatDate(c.fecha_compromiso)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-700">{c.inversion ? formatCLP(c.inversion) : "—"}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select value={c.estado} onValueChange={(v) => updateState(c, v)}>
                          <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {COMMITMENT_ESTADOS.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Semaforo value={c.semaforo} /></TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Link to={`/compromisos/${c.id}`}><Button size="sm" variant="ghost"><ArrowSquareOut size={14} /></Button></Link>
                        <Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash size={14} className="text-red-600" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.some((c) => c.inversion) && (
                    <TableRow className="bg-slate-50">
                      <TableCell colSpan={4} className="text-right text-xs font-semibold text-slate-600">Total monto comprometido</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-slate-900">
                        {formatCLP(items.reduce((s, c) => s + (Number(c.inversion) || 0), 0))}
                      </TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="timeline">
            <div className="mt-4 rounded-md border border-slate-200 bg-white p-6">
              {timeline.length === 0 && <div className="text-sm text-slate-500">Sin compromisos para mostrar.</div>}
              <ol className="relative border-l border-slate-200 pl-6 space-y-6">
                {timeline.map(c => (
                  <li key={c.id}>
                    <div className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full ${c.semaforo === "rojo" ? "bg-red-500" : c.semaforo === "amarillo" ? "bg-amber-500" : "bg-brand-green-500"}`}></div>
                    <div className="text-xs text-slate-500">{formatDate(c.fecha_compromiso) || "Sin fecha"}</div>
                    <div className="font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-600 flex items-center gap-2 mt-0.5">
                      <StatusBadge tone="info">{c.tipo}</StatusBadge>
                      <span>· {c.responsable_interno || "Sin responsable"}</span>
                      <Semaforo value={c.semaforo} />
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
